<template>
  <b-modal
    ref="modal"
    :title="$t('Phone Verification')"
    hide-footer
    @hidden="cancel"
  >
    <b-alert
      v-if="errorMessage"
      show
      variant="danger"
    >
      {{ errorMessage }}
    </b-alert>
    <template v-if="showPasswordStep">
      <p>{{ $t('Enter your account password to confirm this change.') }}</p>
      <b-form-input
        v-model="password"
        type="password"
        autocomplete="current-password"
        autofocus
        :aria-label="$t('Password')"
        @keyup.enter="onPasswordEnter"
      />
      <div class="otp-actions mt-2">
        <b-button
          variant="link"
          class="otp-actions__tertiary"
          @click="cancel"
        >
          {{ $t('Cancel') }}
        </b-button>
        <div class="otp-actions__main">
          <b-button
            variant="primary"
            :disabled="loading || !password"
            @click="submitPassword"
          >
            {{ $t('Send Verification Code') }}
          </b-button>
        </div>
      </div>
    </template>
    <template v-else>
      <p class="text-center">
        {{ $t('We sent a six-digit code to {phone}.', { phone: maskedPhone }) }}
      </p>
      <b-form-input
        v-model="code"
        class="verification-code-input mx-auto"
        maxlength="6"
        inputmode="numeric"
        autocomplete="one-time-code"
        autofocus
        :formatter="codeFormatter"
        :aria-label="$t('Verification Code')"
        @keyup.enter="onCodeEnter"
      />
      <div class="otp-hints mt-1 text-center">
        <small
          v-if="expired"
          class="text-danger d-block"
        >
          {{ $t('The code has expired. Request a new one.') }}
        </small>
        <small
          v-else-if="expiresIn > 0"
          class="text-muted d-block"
        >
          {{ $t('The code expires in {seconds} seconds.', { seconds: expiresIn }) }}
        </small>
        <small
          v-if="cooldown > 0"
          class="text-muted d-block"
        >
          {{ $t('You can request a new code in {seconds} seconds.', { seconds: cooldown }) }}
        </small>
      </div>
      <!-- Item 13: the three actions used to be a two-sided squeeze with the
           resend and verify buttons sharing a bare <span>, so they collided
           and sat at different heights. They are now explicitly ranked --
           tertiary escape hatch on the left, secondary + primary on the
           right. DOM order is deliberately unchanged (change, resend,
           verify); the visual ranking comes from variant and spacing. -->
      <div class="otp-actions mt-2">
        <b-button
          variant="link"
          class="otp-actions__tertiary"
          @click="cancel"
        >
          {{ purpose === 'register' ? $t('Change phone number') : $t('Cancel') }}
        </b-button>
        <div class="otp-actions__main">
          <b-button
            variant="outline-primary"
            :disabled="loading || cooldown > 0 || restartRequired"
            @click="requestChallenge"
          >
            {{ $t('Resend Code') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="loading || expired || !codeValid || restartRequired"
            @click="verify"
          >
            {{ $t('Verify') }}
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from '@axios'
import {
  BAlert, BButton, BFormInput, BModal,
} from 'bootstrap-vue'
import { stripPhone } from '@/libs/phone'
import { OTP_ERROR_KEYS, OTP_ERROR_KEYS_BY_CODE } from '@/libs/otpI18nKeys'
import useJwt from '@/auth/jwt/useJwt'

/* eslint-disable import/extensions, import/no-unresolved, import/no-absolute-path */
const { axiosRoutes: { auth: authRoutes } } = require('/G_CONFIG.js')
/* eslint-enable import/extensions, import/no-unresolved, import/no-absolute-path */

export default {
  components: {
    BAlert, BButton, BFormInput, BModal,
  },
  props: {
    phone: { type: String, required: true },
    purpose: { type: String, required: true },
  },
  data() {
    return {
      challengeId: '',
      code: '',
      password: '',
      cooldown: 0,
      expiresIn: 0,
      expired: false,
      loading: false,
      errorMessage: '',
      timer: null,
      expiryTimer: null,
      torndown: false,
      verifiedSuccessfully: false,
      restartRequired: false,
    }
  },
  computed: {
    codeValid() {
      return /^\d{6}$/.test(this.code)
    },
    // The 'change' purpose requires the account password before a request-otp
    // call can be authorized (CODE-R1-013); 'register' has no account yet, so
    // it skips straight to the code step.
    needsPassword() {
      return this.purpose === 'change'
    },
    showPasswordStep() {
      return this.needsPassword && !this.challengeId
    },
    maskedPhone() {
      const digits = stripPhone(this.phone)
      if (digits.length !== 10) return this.phone
      return `0${digits.slice(0, 1)}•• ••• •• ${digits.slice(-2)}`
    },
  },
  mounted() {
    this.$refs.modal.show()
    if (!this.needsPassword) this.requestChallenge()
  },
  beforeDestroy() {
    // In-flight requests can resolve after teardown; the flag stops their
    // continuations from recreating timers or emitting on a destroyed instance.
    this.torndown = true
    if (this.timer) clearInterval(this.timer)
    if (this.expiryTimer) clearInterval(this.expiryTimer)
  },
  methods: {
    codeFormatter(value) {
      return String(value == null ? '' : value).replace(/\D/g, '').slice(0, 6)
    },
    routePair() {
      return {
        register: [authRoutes.phoneRegisterRequest, authRoutes.phoneRegisterVerify],
        change: [authRoutes.phoneChangeRequest, authRoutes.phoneChangeVerify],
      }[this.purpose]
    },
    errorText(error) {
      const response = error && error.response
      const data = response && response.data ? response.data : {}
      if (response && response.status === 429) return this.$t(OTP_ERROR_KEYS.throttled)
      const mapped = OTP_ERROR_KEYS_BY_CODE[Number(data.errorCode)]
      if (mapped) return this.$t(mapped)
      return data.message || this.$t(OTP_ERROR_KEYS.invalidCode)
    },
    // CODE-R1-013: a factor rotation that requires a restart is reported
    // either as the server's 503/503003, or (on a re-attempt after that
    // state is already recorded) as a synchronous CredentialStateError from
    // useJwt's armFactorPending guard, which never reaches the network.
    isFactorRestartRequired(error) {
      const response = error && error.response
      if (response && response.status === 503 && Number(response.data && response.data.errorCode) === 503003) return true
      return Boolean(error && error.name === 'CredentialStateError' && /restart/i.test(error.message || ''))
    },
    startCountdown(seconds) {
      if (this.torndown) return
      if (this.timer) clearInterval(this.timer)
      this.cooldown = Number(seconds) || 0
      this.timer = setInterval(() => {
        if (this.cooldown > 0) this.cooldown -= 1
        if (this.cooldown <= 0) {
          clearInterval(this.timer)
          this.timer = null
        }
      }, 1000)
    },
    startExpiryCountdown(seconds) {
      if (this.torndown) return
      if (this.expiryTimer) clearInterval(this.expiryTimer)
      this.expiresIn = Number(seconds) || 0
      this.expired = this.expiresIn <= 0
      if (this.expired) return
      this.expiryTimer = setInterval(() => {
        if (this.expiresIn > 0) this.expiresIn -= 1
        if (this.expiresIn <= 0) {
          this.expired = true
          clearInterval(this.expiryTimer)
          this.expiryTimer = null
        }
      }, 1000)
    },
    requestBody() {
      // CODE-R1-013: the 'change' purpose authorizes against the caller's own
      // account (password + the phone being moved to), not a bare `phone` —
      // the server's changeRequest handler requires exactly this shape.
      return this.needsPassword
        ? { password: this.password, newPhone: stripPhone(this.phone) }
        : { phone: stripPhone(this.phone) }
    },
    async requestChallenge() {
      const pair = this.routePair()
      if (!pair) return
      this.loading = true
      this.errorMessage = ''
      try {
        const { data } = await axios.post(pair[0], this.requestBody(), { suppressErrorToast: true })
        if (this.torndown) return
        this.challengeId = data.challengeId
        this.code = data.devCode || ''
        this.startCountdown(data.resendCooldownSeconds)
        this.startExpiryCountdown(data.expiresInSeconds)
        this.$emit('sent', data)
      } catch (error) {
        if (this.torndown) return
        this.errorMessage = this.errorText(error)
        const retry = error.response && error.response.data && error.response.data.retryAfterSeconds
        if (retry) this.startCountdown(retry)
      } finally {
        if (!this.torndown) this.loading = false
      }
    },
    async verify() {
      if (!this.codeValid) return
      const pair = this.routePair()
      this.loading = true
      this.errorMessage = ''
      try {
        const payload = { challengeId: this.challengeId, code: this.code }
        const data = this.needsPassword
          ? await useJwt.runFactorVerification(pair[1], payload)
          : (await axios.post(pair[1], payload, { suppressErrorToast: true })).data
        if (this.torndown) return
        // Mark success before hide(): b-modal's `hidden` event fires for
        // every dismissal path (backdrop click, Esc, programmatic .hide()),
        // and cancel() must not treat a successful verify as a cancellation.
        this.verifiedSuccessfully = true
        this.$emit('verified', data)
        this.$refs.modal.hide()
      } catch (error) {
        if (this.torndown) return
        if (this.isFactorRestartRequired(error)) {
          this.restartRequired = true
          this.errorMessage = this.$t('Verification service is restarting. Please close this window and try again shortly.')
        } else {
          this.errorMessage = this.errorText(error)
        }
      } finally {
        if (!this.torndown) this.loading = false
      }
    },
    onCodeEnter() {
      if (this.loading || this.expired || !this.codeValid || this.restartRequired) return undefined
      return this.verify()
    },
    submitPassword() {
      if (this.loading || !this.password) return undefined
      return this.requestChallenge()
    },
    onPasswordEnter() {
      return this.submitPassword()
    },
    cancel() {
      if (this.verifiedSuccessfully) return
      this.$emit('cancelled')
    },
  },
}
</script>

<style lang="scss" scoped>
/* The compact code field itself is the shared `.verification-code-input`
   rule in assets/scss/style.scss, so this modal and the account-information
   page stay identical. */
.otp-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
}

.otp-actions__main {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
}

/* Without this the Turkish label ("Numarayı değiştir") wraps onto two lines
   and the button grows taller than everything opposite it. */
.otp-actions__tertiary {
  padding-left: 0;
  padding-right: 0;
  white-space: nowrap;
}

.otp-hints small + small {
  margin-top: 0.25rem;
}
</style>
